import React, { useEffect, useState } from "react";
// import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from "@core/layouts/PageHeader";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared//ui/card";
import { Tabs, TabsList, TabsTrigger } from "@shared/ui/tabs";
import {
  ArrowLeft,
  Droplet,
  Plug,
  Flame,
  Car,
  Trash2,
  Users,
  Phone,
  Wallet,
  Database,
  FileText,
  MessageCircle,
  LayoutGrid,
  Upload,
  UserPlus,
  InfoIcon,
} from "lucide-react";
import { UtilityServiceTab } from "../UtilityServiceTab";
import SystemAccessTab from "../system-access/SystemAccessTab";
import ConsumerCategoryComponent from "../category/ConsumerCategoryTab";
import DocumentTab from "../document/DocumentTab";
import { EnhancedRoleVisibilityManager } from "../system-roles/SystemRoles";
import ContractTab from "../contract/ContractTab";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import ParameterRegistryList from "../parameterRegistry/List";

const utilityServiceTypes = [
  {
    id: "water",
    name: "Water",
    icon: Droplet,
    description: "Water utility services and metering",
    units: ["Gallons", "Cubic Meters", "Cubic Feet", "Liters"],
  },
  {
    id: "electric",
    name: "Electric",
    icon: Plug,
    description: "Electricity distribution and metering",
    units: ["kWh", "MWh"],
  },
  {
    id: "gas",
    name: "Gas",
    icon: Flame,
    description: "Natural gas distribution and metering",
    units: ["Therms", "CCF", "MCF", "GJ"],
  },
  {
    id: "wastewater",
    name: "Wastewater",
    icon: Trash2,
    description: "Wastewater collection and treatment",
    units: ["Gallons", "Cubic Meters"],
  },
  {
    id: "hot-water",
    name: "Hot Water",
    icon: Droplet,
    description: "Hot water distribution systems",
    units: ["Gallons", "Cubic Meters"],
  },
  {
    id: "sewer",
    name: "Sewer",
    icon: Trash2,
    description: "Sewer systems and maintenance",
    units: ["Gallons", "Cubic Meters"],
  },
  {
    id: "sub-metering",
    name: "Sub Metering",
    icon: Database,
    description: "Sub-metering solutions for multi-tenant buildings",
    units: ["Various"],
  },
  {
    id: "ev",
    name: "EV Charging",
    icon: Car,
    description: "Electric vehicle charging infrastructure",
    units: ["kWh", "Hours"],
  },
];

const systemAccessModules = [
  {
    id: "call-centers",
    name: "Call Centers",
    icon: Phone,
    description: "Manage customer service call centers",
  },
  {
    id: "consumer-services",
    name: "Consumer Services",
    icon: Users,
    description: "Customer account and service management",
  },
  {
    id: "meter-data",
    name: "Meter Data",
    icon: Database,
    description: "Meter reading and consumption data",
  },
  {
    id: "billing",
    name: "Billing",
    icon: Wallet,
    description: "Invoice generation and payment processing",
  },
  {
    id: "service-orders",
    name: "Service Orders",
    icon: FileText,
    description: "Work order creation and tracking",
  },
  {
    id: "reports",
    name: "Reports & Analytics",
    icon: FileText,
    description: "Business intelligence and data analysis",
  },
  {
    id: "communication",
    name: "Communication Hub",
    icon: MessageCircle,
    description: "Customer notifications and correspondence",
  },
  {
    id: "integration",
    name: "Integration Hub",
    icon: LayoutGrid,
    description: "Third-party system integrations",
  },
  {
    id: "data-upload",
    name: "Data Upload",
    icon: Upload,
    description: "Batch data import capabilities",
  },
  {
    id: "invite-users",
    name: "Invite Users",
    icon: UserPlus,
    description: "User invitation and access management",
  },
];

const CoreSystemSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState("services");

  usePageViewTimer({ pageName: `Core System Setting - Configuration Started` });

  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("System Settings");
  startTracking();

  useEffect(() => {
    logTimeUntilStep(activeTab);
  }, [activeTab, logTimeUntilStep]);
  const [selectedServices, setSelectedServices] = useState<string[]>([
    "water",
    "electric",
  ]);
  const [meteredServices, setMeteredServices] = useState<
    Record<string, boolean>
  >({
    water: true,
    electric: true,
    gas: false,
  });
  const [serviceUnits, setServiceUnits] = useState<Record<string, string>>({
    water: "Gallons",
    electric: "kWh",
  });
  const [selectedModules, setSelectedModules] = useState<string[]>([
    "consumer-services",
    "billing",
  ]);

  const [consumerCategories, setConsumerCategories] = useState<
    Array<{ id: string; name: string; description: string }>
  >([
    {
      id: "residential",
      name: "Residential",
      description: "Individual home consumers",
    },
    {
      id: "commercial",
      name: "Commercial",
      description: "Business and office consumers",
    },
  ]);

  const [consumerSubCategories, setConsumerSubCategories] = useState<
    Array<{ id: string; name: string; parentId: string }>
  >([
    {
      id: "residential-single",
      name: "Single Family",
      parentId: "residential",
    },
    { id: "residential-multi", name: "Multi-Family", parentId: "residential" },
    { id: "commercial-retail", name: "Retail", parentId: "commercial" },
    { id: "commercial-office", name: "Office", parentId: "commercial" },
  ]);

  const [documentTypes, setDocumentTypes] = useState<
    Array<{ id: string; name: string; description: string }>
  >([
    {
      id: "invoice",
      name: "Invoice",
      description: "Customer billing documents",
    },
    {
      id: "contract",
      name: "Contract",
      description: "Service agreements and contracts",
    },
  ]);

  const [documentSubTypes, setDocumentSubTypes] = useState<
    Array<{ id: string; name: string; parentId: string }>
  >([
    { id: "invoice-regular", name: "Regular Invoice", parentId: "invoice" },
    { id: "invoice-final", name: "Final Bill", parentId: "invoice" },
    { id: "contract-new", name: "New Service", parentId: "contract" },
    { id: "contract-renewal", name: "Service Renewal", parentId: "contract" },
  ]);

  const servicesProgress = Math.round(
    (selectedServices.length / utilityServiceTypes.length) * 100
  );
  const modulesProgress = Math.round(
    (selectedModules.length / systemAccessModules.length) * 100
  );
  const categoriesProgress =
    consumerCategories.length > 0 && documentTypes.length > 0
      ? 100
      : consumerCategories.length > 0 || documentTypes.length > 0
      ? 50
      : 0;

  const overallProgress = Math.round(
    (servicesProgress + modulesProgress + categoriesProgress) / 3
  );

  return (
    <div className="transition-opacity duration-500 opacity-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="page-title">Core System Settings</p>
          <p className="page-subtitle">
            Configure your utility services, system access, and organizational
            structure
          </p>
        </div>

        <Button  variant="outline" size="sm">
          <Link to="/utility/utility-setup/utility-admin-dashboard">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Link>
        </Button>
      </div>

      <Card className="mb-6 mt-6">
        <CardContent>
          <div className="bg-accent/30 p-4 rounded-lg border border-accent/30">
            <div className="flex items-start">
              <InfoIcon className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium">Why this matters</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Core system settings establish the foundation of your utility
                  management system. These configurations determine what
                  services you provide, which modules your staff can access, and
                  how your organization classifies consumers and documents.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Tabs
        defaultValue="services"
        className="w-full mt-4"
        tabsListClassName="grid grid-cols-7 w-full md:w-auto mb-6"
        tabComponents={{
          services: {
            label: "Utility Services",
            component: <UtilityServiceTab />,
            className: "flex-1",
          },
          access: {
            label: "System Access",
            component: <SystemAccessTab />,
            className: "flex-1",
          },
          roles: {
            label: "System Roles",
            component: <EnhancedRoleVisibilityManager />,
            className: "flex-1",
          },
          categories: {
            label: "Consumer Categories",
            component: <ConsumerCategoryComponent />,
            className: "flex-1",
          },
          documents: {
            label: "Document Types",
            component: <DocumentTab />,
            className: "flex-1",
          },
          contracts: {
            label: "Contracts",
            component: <ContractTab />,
            className: "flex-1",
          },
          parameterRegistry: {
            label: "Parameter Registry",
            component: <ParameterRegistryList />,
            className: "flex-1",
          },
        }}
      />
    </div>
  );
};

export default CoreSystemSettings;
